import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Stack
} from '@mui/material';
import {
  CheckCircle,
  Error as ErrorIcon,
  ArrowForward
} from '@mui/icons-material';
import { invitationService } from '../services/invitation/invitation.service';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';

const AcceptInvitation: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { refreshInvitations, refreshData } = useOrganization();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [invitationDetails, setInvitationDetails] = useState<any>(null);

  useEffect(() => {
    const acceptInvitation = async () => {
      if (!token) {
        setError('No invitation token provided');
        setLoading(false);
        return;
      }

      try {
        console.log('Accepting invitation with token:', token.substring(0, 8) + '...');

        // Accept the invitation
        const response = await invitationService.acceptInvitationByToken(token);

        console.log('Invitation accepted successfully:', response);
        setInvitationDetails(response);
        setSuccess(true);
        setError(null);

        // Refresh all organization-related data to reflect the new membership
        try {
          await Promise.all([
            refreshInvitations(),
            refreshData()
          ]);
          console.log('Organization data refreshed after invitation acceptance');
        } catch (refreshError) {
          console.error('Failed to refresh organization data:', refreshError);
          // Don't fail the acceptance if refresh fails
        }

        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 3000);
      } catch (err: any) {
        console.error('Failed to accept invitation:', err);

        // Extract error message
        let errorMessage = 'Failed to accept invitation';
        if (err?.response?.data?.error?.message) {
          errorMessage = err.response.data.error.message;
        } else if (err?.message) {
          errorMessage = err.message;
        }

        setError(errorMessage);
        setSuccess(false);
      } finally {
        setLoading(false);
      }
    };

    acceptInvitation();
  }, [token, navigate, refreshInvitations, refreshData]);

  const handleGoToDashboard = () => {
    navigate('/dashboard', { replace: true });
  };

  const handleGoToInvitations = () => {
    navigate('/invitations', { replace: true });
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            {loading && (
              <>
                <CircularProgress size={60} sx={{ mb: 3 }} />
                <Typography variant="h5" gutterBottom>
                  Accepting Invitation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please wait while we process your invitation...
                </Typography>
              </>
            )}

            {!loading && success && (
              <>
                <CheckCircle
                  sx={{
                    fontSize: 80,
                    color: 'success.main',
                    mb: 2
                  }}
                />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  Invitation Accepted!
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  You have successfully joined the team. Redirecting to your dashboard...
                </Typography>
                {invitationDetails?.invitation && (
                  <Box sx={{
                    bgcolor: 'background.default',
                    p: 2,
                    borderRadius: 1,
                    mb: 3
                  }}>
                    {invitationDetails.invitation.organization_name && (
                      <Typography variant="body2" color="text.secondary">
                        Organization: <strong>{invitationDetails.invitation.organization_name}</strong>
                      </Typography>
                    )}
                    {invitationDetails.invitation.team_name && (
                      <Typography variant="body2" color="text.secondary">
                        Team: <strong>{invitationDetails.invitation.team_name}</strong>
                      </Typography>
                    )}
                    {invitationDetails.invitation.role_name && (
                      <Typography variant="body2" color="text.secondary">
                        Role: <strong>{invitationDetails.invitation.role_name}</strong>
                      </Typography>
                    )}
                  </Box>
                )}
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={handleGoToDashboard}
                  fullWidth
                >
                  Go to Dashboard
                </Button>
              </>
            )}

            {!loading && error && (
              <>
                <ErrorIcon
                  sx={{
                    fontSize: 80,
                    color: 'error.main',
                    mb: 2
                  }}
                />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                  Unable to Accept Invitation
                </Typography>
                <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                  {error}
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  This could happen if:
                </Typography>
                <Box component="ul" sx={{ textAlign: 'left', mb: 3, pl: 4 }}>
                  <Typography component="li" variant="body2" color="text.secondary">
                    The invitation has expired
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    The invitation has already been accepted
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    The invitation token is invalid
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    You're not logged in with the invited email address
                  </Typography>
                </Box>
                <Stack spacing={2}>
                  <Button
                    variant="contained"
                    onClick={handleGoToInvitations}
                    fullWidth
                  >
                    View My Invitations
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleGoToDashboard}
                    fullWidth
                  >
                    Go to Dashboard
                  </Button>
                </Stack>
              </>
            )}
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AcceptInvitation;
